import { useRef, useState } from "react";
import { Download, FileText, Trash2, Upload } from "lucide-react";
import { useTran } from "@/hooks";
import { AppDialog, ConfirmDialog } from "@/components/common";
import { Button } from "@/components/ui/button";
import { observer } from "mobx-react-lite";
import { useEntryUIStore } from "../../hooks/useDataEntryStore";
import { uploadAndAttachProofs, removeProof } from "../../operations/proof.ops";
import { authSessionStore } from "@/features/auth/stores/auth-session.store";
import { dataEntryService } from "../../services/data-entry.services";

const formatFileSize = (bytes) => {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let size = bytes;
  while (size >= 1024 && i < units.length - 1) {
    size /= 1024;
    i++;
  }
  return `${size.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

const ProofDialog = observer(() => {
  const { t } = useTran();
  const { proofEntry, updatingEntry, setProofEntry } = useEntryUIStore();
  const fileInputRef = useRef(null);
  const [proofToDelete, setProofToDelete] = useState(null);

  const proofs = proofEntry?.proofs || [];
  const companyId = authSessionStore.user?.companyId;

  const handleClose = () => {
    setProofToDelete(null);
    setProofEntry(null);
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !companyId) return;
    await uploadAndAttachProofs(proofEntry, companyId, files);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleRemove = async () => {
    if (!proofToDelete?.fileId) return;
    const removed = await removeProof(proofEntry, proofToDelete.fileId);
    if (removed) {
      setProofToDelete(null);
    }
  };

  return (
    <>
      <AppDialog
        open={!!proofEntry}
        onOpenChange={(v) => !v && handleClose()}
        title={t("Evidence Files")}
        description={proofEntry?.label || ""}
        size="md"
        footer={
          <div className="flex justify-between gap-2">
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={updatingEntry}
            >
              <Upload size={14} />
              {t("Upload Files")}
            </Button>
            <Button variant="outline" onClick={handleClose}>
              {t("Close")}
            </Button>
          </div>
        }
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={handleFileSelect}
        />

        {proofs.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            {t("No evidence files attached yet")}
          </div>
        ) : (
          <div className="space-y-2">
            {proofs.map((proof) => (
              <div
                key={proof.fileId}
                className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2.5"
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  <FileText
                    size={18}
                    className="shrink-0 text-muted-foreground"
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{proof.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatFileSize(proof.size)}
                      {proof.type && ` \u00b7 ${proof.type}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {proof.url && (
                    <button
                      type="button"
                      onClick={() => dataEntryService.downloadProofFile(proof)}
                      className="p-1.5 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <Download size={15} />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setProofToDelete(proof)}
                    disabled={updatingEntry}
                    className="p-1.5 text-muted-foreground hover:text-destructive transition-colors disabled:opacity-50"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </AppDialog>

      <ConfirmDialog
        open={!!proofToDelete}
        onOpenChange={(open) => !open && setProofToDelete(null)}
        title={t("Delete evidence file")}
        description={t(
          "Are you sure you want to delete this evidence file? This action cannot be undone.",
        )}
        confirmLabel={t("Delete")}
        cancelLabel={t("Cancel")}
        onConfirm={handleRemove}
        loading={updatingEntry}
        tone="danger"
      />
    </>
  );
});

export default ProofDialog;
